import { defineStore } from 'pinia'
import { ref } from 'vue'
import { BleService, type BleDevice } from '../services/bleService'
import { Capacitor } from '@capacitor/core'
import { ScanMode } from '@capacitor-community/bluetooth-le'
import { logger, LogLevel } from '@/services/logger'

export const useBleStore = defineStore('ble', () => {
  const isScanning = ref(false)
  const error = ref<string | null>(null)
  const devices = ref<BleDevice[]>([])
  const bleService = new BleService()

  // Periodic presence detection
  const isDevicePresent = ref(false)
  const isPeriodicScanActive = ref(false)
  const lastSeenTimestamp = ref<number | null>(null)
  let periodicScanInterval: ReturnType<typeof setInterval> | null = null
  let presenceCheckTimeout: ReturnType<typeof setTimeout> | null = null

  // Scan duration and intervals (in milliseconds)
  const SCAN_DURATION = 15000 // Scan for 15 seconds (increased for better detection)
  const SCAN_INTERVAL_SEARCHING = 15000 // Scan every 15 seconds when device NOT found
  const SCAN_INTERVAL_PRESENT = 30000 // Scan every 30 seconds when device IS found
  const PRESENCE_TIMEOUT = 30000 // Consider device absent after 30 seconds of not seeing it
  const MAX_RETRY_ATTEMPTS = 2 // Retry scan this many times if no device found

  const startScanning = async () => {
    try {
      console.log('[BleStore] Starting scan...')
      console.log('[BleStore] Scanning for devices with UUID: 1a6541e5-c692-4558-9554-539760c1f97f')
      error.value = null
      devices.value = []

      await logger.logAdmin(
        LogLevel.INFO,
        'ble',
        'User initiated BLE scan',
        { scanMode: 'manual', targetUUID: '1a6541e5-c692-4558-9554-539760c1f97f' }
      )

      await bleService.startScanning(
        (device: BleDevice) => {
          console.log('[BleStore] Device found:', device)

          // Check if device already exists in the list
          const existingIndex = devices.value.findIndex(d => d.deviceId === device.deviceId)

          if (existingIndex >= 0) {
            // Update existing device (e.g., RSSI might have changed)
            devices.value[existingIndex] = device
            console.log('[BleStore] Device updated:', device.deviceId)
          } else {
            // Add new device
            devices.value.push(device)
            console.log('[BleStore] Device added:', device.deviceId)
          }
        },
        (err: Error) => {
          error.value = err.message
          console.error('[BleStore] Scan error:', err)
          logger.logAdmin(
            LogLevel.ERROR,
            'ble',
            'BLE scan error',
            { error: err.message },
            err
          ).catch(console.error)
        },
        {
          allowDuplicates: true, // Allow continuous updates
          scanMode: ScanMode.SCAN_MODE_BALANCED // More consistent detection
        }
      )

      isScanning.value = true
      console.log('[BleStore] Scan started successfully')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      console.error('[BleStore] Failed to start scan:', errorMessage)
      error.value = errorMessage
      isScanning.value = false

      await logger.logAdmin(
        LogLevel.ERROR,
        'ble',
        'Failed to start manual BLE scan',
        { error: errorMessage },
        err instanceof Error ? err : undefined
      )

      throw err
    }
  }

  const stopScanning = async () => {
    try {
      console.log('[BleStore] Stopping scan...')
      await bleService.stopScanning()
      isScanning.value = false
      console.log('[BleStore] Scan stopped successfully')
    } catch (err) {
      console.error('[BleStore] Error stopping scan:', err)
      isScanning.value = false
    }
  }

  const clearDevices = () => {
    devices.value = []
  }

  // Mark device as present (called when device is detected)
  const markDevicePresent = () => {
    const wasPresent = isDevicePresent.value
    isDevicePresent.value = true
    lastSeenTimestamp.value = Date.now()

    // Log presence change
    if (!wasPresent) {
      logger.logAdmin(
        LogLevel.INFO,
        'ble',
        'BLE device presence detected',
        {
          lastSeen: new Date(Date.now()).toISOString(),
          presenceTimeout: `${PRESENCE_TIMEOUT / 1000}s`
        }
      ).catch(console.error)
    }

    // Clear any existing timeout
    if (presenceCheckTimeout) {
      clearTimeout(presenceCheckTimeout)
    }

    // Set timeout to mark device as absent if not seen again
    presenceCheckTimeout = setTimeout(() => {
      console.log('[BleStore] Device presence timeout - marking as absent')
      isDevicePresent.value = false

      logger.logAdmin(
        LogLevel.WARN,
        'ble',
        'BLE device presence lost (timeout)',
        {
          timeout: `${PRESENCE_TIMEOUT / 1000}s`,
          lastSeen: lastSeenTimestamp.value ? new Date(lastSeenTimestamp.value).toISOString() : null
        }
      ).catch(console.error)
    }, PRESENCE_TIMEOUT)
  }

  // Schedule next scan with dynamic interval based on device presence
  const scheduleNextScan = () => {
    if (!isPeriodicScanActive.value) return

    // Clear existing interval if any
    if (periodicScanInterval) {
      clearInterval(periodicScanInterval)
      periodicScanInterval = null
    }

    // Choose interval based on device presence
    const interval = isDevicePresent.value ? SCAN_INTERVAL_PRESENT : SCAN_INTERVAL_SEARCHING

    console.log(`[BleStore] Scheduling next scan in ${interval / 1000}s (device ${isDevicePresent.value ? 'present' : 'not found'})`)

    periodicScanInterval = setTimeout(async () => {
      await performPresenceScan()
      scheduleNextScan() // Schedule the next one after this scan completes
    }, interval) as any
  }

  // Periodic scan for presence detection
  const startPeriodicScan = async () => {
    const platform = Capacitor.getPlatform()
    if (platform === 'web') {
      console.log('[BleStore] Periodic scan not available on web platform')
      await logger.logAdmin(
        LogLevel.WARN,
        'ble',
        'Periodic BLE scan not available on web platform',
        { platform }
      )
      return
    }

    if (isPeriodicScanActive.value) {
      console.log('[BleStore] Periodic scan already active')
      return
    }

    console.log('[BleStore] Starting periodic presence detection...')
    await logger.logAdmin(
      LogLevel.INFO,
      'ble',
      'Starting periodic BLE presence detection',
      {
        scanDuration: `${SCAN_DURATION / 1000}s`,
        intervalSearching: `${SCAN_INTERVAL_SEARCHING / 1000}s`,
        intervalPresent: `${SCAN_INTERVAL_PRESENT / 1000}s`,
        presenceTimeout: `${PRESENCE_TIMEOUT / 1000}s`
      }
    )

    isPeriodicScanActive.value = true

    // Perform initial scan
    await performPresenceScan()

    // Schedule next scan with dynamic interval
    scheduleNextScan()
  }

  // Stop periodic scanning
  const stopPeriodicScan = async () => {
    console.log('[BleStore] Stopping periodic scan...')

    await logger.logAdmin(
      LogLevel.INFO,
      'ble',
      'Stopping periodic BLE presence detection',
      {
        wasActive: isPeriodicScanActive.value,
        wasDevicePresent: isDevicePresent.value
      }
    )

    if (periodicScanInterval) {
      clearTimeout(periodicScanInterval as any)
      clearInterval(periodicScanInterval)
      periodicScanInterval = null
    }

    if (presenceCheckTimeout) {
      clearTimeout(presenceCheckTimeout)
      presenceCheckTimeout = null
    }

    // Stop any active scan
    if (isScanning.value) {
      await bleService.stopScanning()
      isScanning.value = false
    }

    isPeriodicScanActive.value = false
    isDevicePresent.value = false
    console.log('[BleStore] Periodic scan stopped')
  }

  // Perform a single presence detection scan with retry logic
  const performPresenceScan = async (retryAttempt = 0) => {
    if (isScanning.value) {
      console.log('[BleStore] Already scanning, skipping this cycle')
      return
    }

    console.log(`[BleStore] Performing presence scan (attempt ${retryAttempt + 1}/${MAX_RETRY_ATTEMPTS + 1})...`)

    if (retryAttempt === 0) {
      await logger.logAdmin(
        LogLevel.DEBUG,
        'ble',
        'Starting periodic presence scan',
        { scanDuration: `${SCAN_DURATION / 1000}s` }
      )
    } else {
      await logger.logAdmin(
        LogLevel.DEBUG,
        'ble',
        `Retrying presence scan (attempt ${retryAttempt + 1}/${MAX_RETRY_ATTEMPTS + 1})`,
        { scanDuration: `${SCAN_DURATION / 1000}s` }
      )
    }

    try {
      let deviceFound = false
      let scanTimeout: ReturnType<typeof setTimeout> | null = null

      await bleService.startScanning(
        async (device: BleDevice) => {
          console.log('[BleStore] Device detected in presence scan:', device.deviceId)
          deviceFound = true
          markDevicePresent()

          // Stop scanning immediately when device is found
          if (scanTimeout) {
            clearTimeout(scanTimeout)
            scanTimeout = null
          }

          if (isScanning.value) {
            await bleService.stopScanning()
            isScanning.value = false
            console.log('[BleStore] Presence scan completed early - device found!')
          }
        },
        (err: Error) => {
          console.error('[BleStore] Presence scan error:', err)
          logger.logAdmin(
            LogLevel.ERROR,
            'ble',
            'Presence scan error',
            { error: err.message, retryAttempt: retryAttempt + 1 },
            err
          ).catch(console.error)
        },
        {
          allowDuplicates: true, // Allow continuous device detection
          scanMode: undefined // Use default LOW_LATENCY mode
        }
      )

      isScanning.value = true

      // Stop scanning after SCAN_DURATION if device not found
      scanTimeout = setTimeout(async () => {
        if (isScanning.value) {
          await bleService.stopScanning()
          isScanning.value = false
          console.log('[BleStore] Presence scan completed. Device found:', deviceFound)

          if (!deviceFound) {
            await logger.logAdmin(
              LogLevel.WARN,
              'ble',
              `Presence scan completed - no device found (attempt ${retryAttempt + 1}/${MAX_RETRY_ATTEMPTS + 1})`,
              {
                scanDuration: `${SCAN_DURATION / 1000}s`,
                willRetry: retryAttempt < MAX_RETRY_ATTEMPTS
              }
            )
          }

          // If device not found and we have retries left, try again immediately
          if (!deviceFound && retryAttempt < MAX_RETRY_ATTEMPTS) {
            console.log('[BleStore] Device not found, retrying immediately...')
            // Small delay before retry to allow BLE stack to reset
            setTimeout(() => {
              performPresenceScan(retryAttempt + 1)
            }, 1000)
          }
        }
      }, SCAN_DURATION)
    } catch (err) {
      console.error('[BleStore] Failed to perform presence scan:', err)
      isScanning.value = false

      await logger.logAdmin(
        LogLevel.ERROR,
        'ble',
        'Failed to perform presence scan',
        {
          error: err instanceof Error ? err.message : 'Unknown error',
          retryAttempt: retryAttempt + 1,
          willRetry: retryAttempt < MAX_RETRY_ATTEMPTS
        },
        err instanceof Error ? err : undefined
      )

      // Retry on error if we have retries left
      if (retryAttempt < MAX_RETRY_ATTEMPTS) {
        console.log('[BleStore] Scan failed, retrying immediately...')
        setTimeout(() => {
          performPresenceScan(retryAttempt + 1)
        }, 1000)
      }
    }
  }

  return {
    isScanning,
    error,
    devices,
    isDevicePresent,
    isPeriodicScanActive,
    lastSeenTimestamp,
    startScanning,
    stopScanning,
    clearDevices,
    startPeriodicScan,
    stopPeriodicScan
  }
})
